import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, BarChart3, Users, ChevronRight } from '../../icons';
import { getMyProfile, type ProfileDto } from '../../api/users';
// 오늘·이번 달·이상 근태 — **이미 있는 라우트**를 그대로 부른다(서버 0).
import {
  getTeamAttendance, getAttendanceRange,
  // 결근 (판 2-b) — 까닭의 말과 「고칠 수 있는가」는 API 모듈 한 곳이 갖는다.
  UNCOUNTABLE_TEXT, topUncountable,
  type TeamAttendanceDto, type AttendanceRangeDto, type AbsenceTotalsDto,
} from '../../api/hr-team';
import { fetchAnomalies, type AnomalyRow } from '../../api/hr-anomalies';
import { listDocuments } from '../../api/approval';
import { MEMBER_STATE_LABEL, MEMBER_STATE_TONE, formatSpan } from '../../lib/team-attendance';
import { StatTile, StatTiles } from '../../components/shared';
import './TeamScopePage.css';

/**
 * 우리 조직 — 팀장 · 부서장 · 대표가 **자기 범위**를 보는 자리 (2026-08-28 · 자리 재정의 판 5).
 *
 * ★★ **왜 메뉴 권한으로 못 하나.** 팀장은 「그 팀의 리더」라는 **개인 지정**이라, 메뉴 권한을
 *    부서로 켜면 **팀원 전원**에게 뜬다. 그래서 축이 셋이고 이것은 «조직 지정» 축이다.
 *    판정은 서버가 한다 — `GET /users/me` 의 `orgScope`(팀장·부서장·대표를 이미 있는 칸에서 읽는다).
 *
 * ★★ **보기와 결재만 준다 — 기준 설정은 주지 않는다.** 팀장에게 근무제·연차 규칙을 열면
 *    회사가 정한 기준이 팀마다 갈린다. 그것은 담당자(인사·근태)의 자리다.
 *
 * ★ **옛 주소를 살려 둔다** — 팀 근태(`/erp/attendance/team`)·팀원 인사정보
 *   (`/erp/hr/me/team-hr`)는 그대로 두고 여기서 **길만** 낸다. 대시보드 위젯이 옛 주소를 가리킨다.
 *
 * ── 조직 홈 (2026-08-31 · 시안 D1) ──────────────────────────────────────────
 * ★★★ **착지 화면이 링크 넉 줄이었다.** 팀장이 이 메뉴를 여는 이유는 대개 셋인데
 *    (오늘 누가 안 왔나 · 내가 결재할 것 · 뭐가 어긋났나) 그 셋이 **한 번 더 눌러야** 나왔다.
 *    시안이 착지 화면을 「오늘 처리해야 할 것」으로 그린 이유가 그것이다.
 *
 * ★★ **새 서버가 없다.** 오늘 표·요약은 `GET /hr/attendance/team`(팀 근태 화면이 쓰던 것),
 *    이상은 `/hr/attendance/anomalies`, 결재 수는 결재 대기함이 이미 준다. **화면만 없었다.**
 * ★★ **휴가 «종류»는 안 나온다** — 서버가 아예 안 보낸다(병가·경조사는 알 필요 없는 정보).
 *    가려 놓고 안 적으면 「가려진 것」인지 「없는 것」인지 모르므로 화면이 그 사실을 적는다.
 * ⚠ **「다가오는 휴가」는 못 만들었다** — 시안에 있지만 **앞으로의 휴가를 주는 라우트가 없다**
 *   (근태 API 는 지나간 날과 오늘만 준다). 없는 것을 지어 그리지 않는다.
 */

const SCOPE_LABEL: Record<string, string> = {
  ALL: '전사', DIVISION: '우리 부서', TEAM: '우리 팀',
};

/**
 * 결근을 적는다 — **못 세는 날은 «0» 이 아니다** (판 2-b · 2026-09-11).
 *
 * ★★★ 정본 P7 문구 규칙이 이 경우를 말 그대로 적어 두었다:
 *    「숫자 — 셀 수 없는 것은 적지 않는다. **『결근 0』 대신 『집계할 수 없음』**」
 *    「모르는 값 — 0 으로 적지 않는다」
 *    둘을 같은 「0」으로 보이면 사람들은 **결근이 없다고 읽는다.**
 * ★★ **옛 서버는 이 칸을 안 준다** — 그때는 결근을 **아예 말하지 않는다**. 없는 것을
 *    「0」으로 지어내면 그것이 가장 나쁜 거짓말이다.
 * ★ 색만으로 뜻을 만들지 않는다(정본 P5) — 글자가 스스로 무엇인지 말한다.
 */
function AbsenceText({ absence }: { absence?: AbsenceTotalsDto }): JSX.Element | null {
  if (!absence) return null;

  if (!absence.countable) {
    const top = topUncountable(absence.uncountableBy);
    return (
      <>
        {' · '}
        <span className="tscope__uncount">
          결근 집계할 수 없음{top ? ` — ${UNCOUNTABLE_TEXT[top].text}` : ''}
        </span>
      </>
    );
  }

  const top = absence.uncountableDays > 0 ? topUncountable(absence.uncountableBy) : null;
  return (
    <>
      {' · '}
      <span className={absence.absentDays > 0 ? 'tscope__absent' : undefined}>
        결근 {absence.absentDays}
      </span>
      {top && (
        <span className="tscope__uncount">
          {` (${absence.uncountableDays}일은 못 셈 — ${UNCOUNTABLE_TEXT[top].text})`}
        </span>
      )}
    </>
  );
}

export function TeamScopePage(): JSX.Element {
  const [me, setMe] = useState<ProfileDto | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'folded'>('loading');

  useEffect(() => {
    let alive = true;
    getMyProfile()
      .then((p) => { if (alive) { setMe(p); setState('ok'); } })
      .catch(() => { if (alive) setState('folded'); });
    return () => { alive = false; };
  }, []);

  // 옛 서버는 `orgScope` 를 안 준다 — 그때는 «없음» 으로 본다(있는 척하지 않는다).
  const scope = me?.orgScope ?? 'NONE';
  const teams = me?.leadTeams ?? [];
  const divisions = me?.leadDivisions ?? [];

  /** 내가 무엇을 이끌고 있나 — 「우리 팀」이 어디인지 이름으로 말한다. */
  const whatIlead = scope === 'ALL' ? '회사 전체'
    : divisions.length > 0 ? divisions.map((d) => d.name).join(' · ')
      : teams.map((t) => t.name).join(' · ');

  /* ── 조직 홈 (2026-08-31 · 시안 D1) ───────────────────────────────────────── */
  const [today, setToday] = useState<TeamAttendanceDto | null>(null);
  const [month, setMonth] = useState<AttendanceRangeDto | null>(null);
  const [anomalies, setAnomalies] = useState<AnomalyRow[] | null>(null);
  const [inbox, setInbox] = useState<number | null>(null);
  /** 고른 팀 — 팀이 여럿인 부서장·대표를 위해. 비우면 서버가 정한 기본 팀. */
  const [teamId, setTeamId] = useState<string | null>(null);

  /**
   * ★ 넷을 **따로** 받는다(`allSettled`) — 하나가 403·404 여도 나머지는 그린다.
   *   근태를 못 보는 사람에게도 결재 수는 뜨고, 그 반대도 된다.
   */
  const loadHome = useCallback((tid: string | null) => {
    void Promise.allSettled([
      getTeamAttendance(tid ? { teamId: tid } : undefined),
      getAttendanceRange({ period: 'month', ...(tid ? { teamId: tid } : {}) }),
      fetchAnomalies(),
      listDocuments('incoming', { limit: 50 }),
    ]).then(([t, m, a, d]) => {
      setToday(t.status === 'fulfilled' ? t.value : null);
      setMonth(m.status === 'fulfilled' ? m.value : null);
      setAnomalies(a.status === 'fulfilled' ? a.value.rows : null);
      setInbox(d.status === 'fulfilled' ? d.value.items.length : null);
    });
  }, []);

  useEffect(() => {
    if (state !== 'ok' || scope === 'NONE') return;
    loadHome(teamId);
  }, [state, scope, teamId, loadHome]);

  const members = today?.members ?? [];
  const sum = today?.summary;
  /** 이상 — 내 범위의 사람만 남긴다(서버는 볼 수 있는 만큼 준다). */
  const myAnomalies = useMemo(() => {
    if (!anomalies) return [];
    if (!today?.team) return anomalies;
    const ids = new Set(members.map((m) => m.userId));
    return anomalies.filter((r) => ids.has(r.userId));
  }, [anomalies, today, members]);
  const openCount = myAnomalies.filter((r) => r.kind === 'MISSING_CHECKOUT').length;
  const absentNames = myAnomalies.filter((r) => r.kind !== 'MISSING_CHECKOUT').map((r) => r.name);

  const teamLabel = today?.team?.name ?? whatIlead;

  return (
    <div className="tscope">
      <header className="tscope__head">
        <h1>우리 조직</h1>
        <p>
          업무 › 우리 조직 ·
          {state === 'ok' && scope !== 'NONE'
            ? ` ${SCOPE_LABEL[scope] ?? ''} 범위로 봅니다 — ${whatIlead}`
            : ' 팀장 · 부서장 · 대표가 자기 범위를 보는 자리입니다'}
        </p>
      </header>

      {state === 'loading' && <p className="tscope__empty">불러오는 중…</p>}

      {state === 'folded' && (
        <p className="tscope__empty">내 정보를 불러오지 못했습니다 — 잠시 후 다시 열어 주세요.</p>
      )}

      {state === 'ok' && scope === 'NONE' && (
        <div className="tscope__note">
          <AlertCircle size={14} aria-hidden />
          <span>
            이 화면은 <b>팀장 · 부서장 · 대표</b>에게 열립니다. 조직도에서 지정하면 보입니다 —
            <b> 권한을 따로 주는 것이 아니라</b>, 조직에서 그 자리를 맡는 것으로 정해집니다.
          </span>
        </div>
      )}

      {state === 'ok' && scope !== 'NONE' && (
        <>
          {/* 팀이 여럿이면 고른다 — 부서장·대표는 팀이 여러 개다. */}
          {(today?.teams.length ?? 0) > 1 && (
            <div className="tscope__tabs" role="tablist" aria-label="팀">
              {today!.teams.map((t) => (
                <button
                  key={t.id} type="button" role="tab"
                  aria-selected={t.id === (today?.team?.id ?? '')}
                  className={`tscope__tab${t.id === (today?.team?.id ?? '') ? ' is-on' : ''}`}
                  onClick={() => setTeamId(t.id)}
                >{t.name}</button>
              ))}
            </div>
          )}

          {/* ── 오늘 숫자 다섯 — 팀장이 이 메뉴를 여는 이유 ── */}
          {/*
            ★ 정본 `StatTile`(D9-2)로 갈아탔다 (2026-09-06). 라벨이 위였던 것이 **숫자가 위**로
              바뀐다 — 타일의 일은 숫자를 먼저 보이는 것이다(정본 결정 · 칩만 이름이 먼저다).
            ★ 손을 써야 하는 숫자만 물들인다(`warning`) — 다 칠하면 어느 것이 급한지 안 보인다.
          */}
          <StatTiles className="tscope__stats" ariaLabel="오늘 요약">
            <StatTile
              value={sum ? sum.working + sum.off : null} unit={`/ ${sum?.total ?? 0}`} label="출근"
            />
            <StatTile value={sum?.onLeave ?? null} label="휴가" />
            <StatTile
              tone={(sum?.absent ?? 0) > 0 ? 'warning' : 'default'}
              value={sum?.absent ?? null} label="미출근"
            />
            <StatTile
              tone={(inbox ?? 0) > 0 ? 'warning' : 'default'}
              value={inbox ?? null} label="내가 결재할 것"
            />
            {/* ★★ **못 받으면 이 칸이 통째로 사라진다** — 이상 근태는 전사를 보는 사람만 받는다
                (팀장은 403). 「—」로 두면 «0 인가 못 보는 건가»를 알 수 없고, 그 옆에
                「어긋난 것 없음」까지 적으면 **모르는 것을 없다고 말하는 것**이 된다.
                팀 근태 화면의 전사 요약과 같은 규칙이다(한 번 물어보고 되면 그린다). */}
            {anomalies && (
              <StatTile
                tone={openCount > 0 ? 'warning' : 'default'}
                value={openCount}
                /* ★ 「퇴근 누락」만 누계다 — 오늘 안 찍은 사람은 대개 아직 일하는 중이라 이상이 아니다. */
                label={<>퇴근 누락<span className="tscope__sub"> 누계</span></>}
              />
            )}
          </StatTiles>

          {/* ── 오늘 우리 팀 ── */}
          <section className="tscope__box" aria-label="오늘 우리 팀">
            <div className="tscope__boxhead">
              <h2>오늘 {teamLabel ? `· ${teamLabel}` : ''}</h2>
              <span className="tscope__sp" />
              <Link className="tscope__more" to="/erp/attendance/team">근태 자세히 <ChevronRight size={13} aria-hidden /></Link>
            </div>
            {today === null ? (
              <p className="tscope__empty">오늘 근태를 불러오지 못했습니다 — 볼 권한이 없거나 잠시 후 다시 열어 주세요.</p>
            ) : members.length === 0 ? (
              <p className="tscope__empty">이 조직에 인원이 없습니다.</p>
            ) : (
              <div className="tscope__tablewrap">
                <table className="tscope__table">
                  <thead>
                    <tr>
                      <th scope="col">이름</th><th scope="col">직위</th>
                      <th scope="col">출근</th><th scope="col">퇴근</th>
                      <th scope="col">근무</th><th scope="col">상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m) => (
                      <tr key={m.userId}>
                        <td><b>{m.name}</b>{m.isLeader && <span className="tscope__lead">팀장</span>}</td>
                        <td>{m.position ?? '—'}</td>
                        <td className="mono">{m.checkIn ?? '—'}</td>
                        <td className="mono">{m.checkOut ?? '—'}</td>
                        <td className="mono">{m.workedMinutes > 0 ? formatSpan(m.workedMinutes) : '—'}</td>
                        <td>
                          <span className={`tscope__state is-${MEMBER_STATE_TONE[m.state]}`}>
                            {MEMBER_STATE_LABEL[m.state]}
                          </span>
                          {m.late && <span className="tscope__late">지각</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/* ★★ 가려 놓고 안 적으면 «가려진 것»인지 «없는 것»인지 알 수 없다. */}
            <p className="tscope__foot">
              <b>휴가 종류는 여기 안 나옵니다</b> — 어떤 휴가인지는 결재 문서 안에만 있습니다.
              조회는 <b>기록에 남습니다</b>.
            </p>
          </section>

          {/* ── 내가 처리할 것 ── */}
          <section className="tscope__box" aria-label="내가 처리할 것">
            <div className="tscope__boxhead"><h2>내가 처리할 것</h2></div>
            <div className="tscope__todo">
              {/* ★ 「내가 결재할 차례」는 **결재 대기**다 (2026-08-31 · 결재에 주소가 생겼다). */}
              <Link className="tscope__todoitem" to="/approval/pending">
                <b>결재 {inbox ?? '—'}</b>
                <span>내가 결재할 차례인 문서</span>
              </Link>
              {anomalies && (
                <Link className={`tscope__todoitem${myAnomalies.length ? ' is-warn' : ''}`} to="/erp/attendance/anomalies">
                  <b>이상 {myAnomalies.length}</b>
                  <span>
                    {absentNames.length > 0
                      ? `미출근 · ${absentNames.slice(0, 2).join(' · ')}${absentNames.length > 2 ? ` 외 ${absentNames.length - 2}` : ''}`
                      : openCount > 0 ? `퇴근 누락 ${openCount}건` : '어긋난 것 없음'}
                  </span>
                </Link>
              )}
            </div>
          </section>

          {/* ── 이번 달 ── */}
          {month && month.members.length > 0 && (
            <section className="tscope__box" aria-label="이번 달 근무">
              <div className="tscope__boxhead">
                <h2>이번 달 근무</h2>
                <span className="tscope__sub"> {month.from} ~ {month.to}</span>
              </div>
              <div className="tscope__bars">
                {month.members.map((r) => (
                  <div className="tscope__bar" key={r.userId}>
                    <span className="tscope__barname">{r.name}</span>
                    <span className="tscope__barval mono">{formatSpan(r.creditedMinutes)}</span>
                    <span className="tscope__barmeta">
                      나온 날 {r.workedDays}
                      {r.lateDays > 0 && <> · 지각 {r.lateDays}</>}
                      {r.leaveDays > 0 && <> · 휴가 {r.leaveDays}</>}
                      <AbsenceText absence={r.absence} />
                    </span>
                  </div>
                ))}
              </div>
              {/*
                ★★★ **무엇을 센 것인가를 적는다** — 정의 없이 숫자만 있으면 사람들은 저마다 다른
                   것을 떠올린다. 정본 P7: 「숫자 — 셀 수 없는 것은 적지 않는다」.
                ★★ 요일을 **고르지 않은** 근무제는 월~금으로 본다는 사실도 함께 적는다.
                   (판 2 의 마이그레이션이 기존 근무제에 월~금을 채웠는데, 그것은 «회사가 고른 값»이
                   아니라 «되돌아간 기본»이다 — 화면이 말하지 않으면 아무도 모른다.)
              */}
              {month.members.some((m) => m.absence) && (
                <p className="tscope__absnote">
                  <b>결근</b>은 근무요일인데 출근 기록도 사유도 없는 날입니다.
                  휴가 · 출장 · 회사 휴일은 결근이 아니고, <b>오늘은 아직 세지 않습니다</b>.
                  요일을 따로 고르지 않은 근무제는 월~금으로 봅니다.
                  {' '}
                  {/*
                    ★ A-2(2026-09-11) — 이 문장은 **근태 현황 도움말에서 옮겨 왔다.**
                      그 화면은 결근을 보여 주지 않는데 설명만 있었다.
                  */}
                  입사일이나 근무제가 없어 물을 수 없는 날은 <b>「집계할 수 없음」</b>으로 따로 셉니다 —
                  0 으로 적으면 결근이 없다는 말이 되기 때문입니다.
                </p>
              )}
            </section>
          )}

          <div className="tscope__cards">
            <Link className="tscope__card" to="/erp/attendance/team">
              <span className="tscope__ico" aria-hidden><BarChart3 size={17} /></span>
              <span className="tscope__body">
                <b>근태 현황</b>
                <span>오늘 · 이상 근태 · 월별</span>
              </span>
            </Link>
            <Link className="tscope__card" to="/erp/hr/me/team-hr">
              <span className="tscope__ico" aria-hidden><Users size={17} /></span>
              <span className="tscope__body">
                <b>부서원 인사정보</b>
                <span>신상 · 소속 · 이번 달 근태</span>
              </span>
            </Link>
          </div>

          {/* ★ 기준 설정은 여기 없다 — 그 자리는 담당자(인사 · 근태)의 것이다. */}
          <p className="tscope__foot">
            보기와 결재까지입니다 — <b>근무제 · 연차 규칙 같은 기준</b>은 인사 · 근태 담당자가 정합니다.
            팀마다 기준이 갈리면 같은 회사에서 다른 답이 나옵니다.
          </p>
        </>
      )}
    </div>
  );
}
